import {UniversalLoginInstance} from 'app/modules/auth/types/universalLogin'
import {isUniversalLoginHost} from 'common/HelperFunctions'
import {setUniversalLoginCanonicalDomain} from 'common/helpers/universalLoginHandoff'
import {
  applyUniversalLoginRedirect,
  isUniversalLoginConflict,
} from 'common/helpers/universalLoginResponse'
import {useCallback, useState} from 'react'
import {useDispatch} from 'react-redux'
import {universalLogin, universalLoginWithGoogle} from 'setup/redux/Actions/SignIn/login'

import {armLoginTimeout} from '../loginTimeout'
import {useAiLoginError} from './useAiLoginError'

type PendingAuth =
  | {mode: 'password'; email: string; password: string}
  | {mode: 'google'; idToken: string}

type SubmitCallbacks = {
  setStatus?: (status: string) => void
  setSubmitting?: (isSubmitting: boolean) => void
  onLoadingChange?: (loading: boolean) => void
}

export function useUniversalLogin() {
  const dispatch = useDispatch()
  const [showSchoolPicker, setShowSchoolPicker] = useState(false)
  const [schoolInstances, setSchoolInstances] = useState<UniversalLoginInstance[]>([])
  const [pendingAuth, setPendingAuth] = useState<PendingAuth | null>(null)
  const [pickerLoading, setPickerLoading] = useState(false)

  const {aiLoginError, emailLoginOnly, clearAiLoginError, showEmailLoginForm, processAiLoginError} =
    useAiLoginError()

  const finishCallbacks = useCallback((callbacks?: SubmitCallbacks) => {
    callbacks?.onLoadingChange?.(false)
    callbacks?.setSubmitting?.(false)
  }, [])

  const handleConflict = useCallback(
    (res: any, pending: PendingAuth, callbacks?: SubmitCallbacks) => {
      setSchoolInstances(res.result.instances)
      setPendingAuth(pending)
      setShowSchoolPicker(true)
      setPickerLoading(false)
      finishCallbacks(callbacks)
      callbacks?.setStatus?.('')
    },
    [finishCallbacks]
  )

  const handleLoginFailure = useCallback(
    (
      res: any,
      pending: PendingAuth,
      callbacks: SubmitCallbacks | undefined,
      fallbackMessage: string
    ) => {
      const outcome = processAiLoginError(
        res,
        (message) => callbacks?.setStatus?.(message),
        fallbackMessage
      )

      if (outcome === 'conflict') {
        handleConflict(res, pending, callbacks)
        return
      }

      if (outcome === 'handled') {
        callbacks?.setStatus?.('')
      }

      finishCallbacks(callbacks)
    },
    [finishCallbacks, handleConflict, processAiLoginError]
  )

  const submitUniversalLogin = useCallback(
    (email: string, password: string, callbacks?: SubmitCallbacks, applicationId?: number) => {
      const {onLoadingChange} = callbacks ?? {}
      clearAiLoginError()
      onLoadingChange?.(true)

      const cancelTimeout = armLoginTimeout({
        setLoading: onLoadingChange,
        setSubmitting: callbacks?.setSubmitting,
        setStatus: callbacks?.setStatus,
      })

      dispatch(
        universalLogin(
          email,
          password,
          (res: any) => {
            cancelTimeout()
            if (!applyUniversalLoginRedirect(res?.result)) {
              callbacks?.setStatus?.('Login succeeded but no redirect URL was returned.')
              finishCallbacks(callbacks)
            }
          },
          (res: any) => {
            cancelTimeout()
            if (isUniversalLoginConflict(res)) {
              handleConflict(res, {mode: 'password', email, password}, callbacks)
              return
            }
            handleLoginFailure(
              res,
              {mode: 'password', email, password},
              callbacks,
              'Invalid username or password'
            )
          },
          applicationId
        )
      )
    },
    [clearAiLoginError, dispatch, finishCallbacks, handleConflict, handleLoginFailure]
  )

  const submitUniversalGoogleLogin = useCallback(
    (idToken: string, callbacks?: SubmitCallbacks, applicationId?: number) => {
      const {onLoadingChange} = callbacks ?? {}
      clearAiLoginError()
      onLoadingChange?.(true)

      const cancelTimeout = armLoginTimeout({
        setLoading: onLoadingChange,
        setSubmitting: callbacks?.setSubmitting,
        setStatus: callbacks?.setStatus,
      })

      dispatch(
        universalLoginWithGoogle(
          idToken,
          (res: any) => {
            cancelTimeout()
            if (!applyUniversalLoginRedirect(res?.result)) {
              callbacks?.setStatus?.('Google sign-in succeeded but no redirect URL was returned.')
              finishCallbacks(callbacks)
            }
          },
          (res: any) => {
            cancelTimeout()
            if (isUniversalLoginConflict(res)) {
              handleConflict(res, {mode: 'google', idToken}, callbacks)
              return
            }
            handleLoginFailure(res, {mode: 'google', idToken}, callbacks, 'Google sign-in failed')
          },
          applicationId
        )
      )
    },
    [clearAiLoginError, dispatch, finishCallbacks, handleConflict, handleLoginFailure]
  )

  const handleSchoolSelect = useCallback(
    (instance: UniversalLoginInstance, callbacks?: SubmitCallbacks) => {
      if (!pendingAuth) return
      if (instance.canonicalDomain) {
        setUniversalLoginCanonicalDomain(instance.canonicalDomain)
      }
      setPickerLoading(true)

      const wrappedCallbacks = {
        ...callbacks,
        onLoadingChange: (loading: boolean) => {
          setPickerLoading(loading)
          callbacks?.onLoadingChange?.(loading)
        },
      }

      if (pendingAuth.mode === 'google') {
        submitUniversalGoogleLogin(pendingAuth.idToken, wrappedCallbacks, instance.applicationId)
      } else {
        submitUniversalLogin(
          pendingAuth.email,
          pendingAuth.password,
          wrappedCallbacks,
          instance.applicationId
        )
      }
    },
    [pendingAuth, submitUniversalGoogleLogin, submitUniversalLogin]
  )

  const closeSchoolPicker = useCallback(() => {
    if (pickerLoading) return
    setShowSchoolPicker(false)
    setSchoolInstances([])
    setPendingAuth(null)
  }, [pickerLoading])

  return {
    isUniversalLoginHost: isUniversalLoginHost(),
    showSchoolPicker,
    schoolInstances,
    pickerLoading,
    aiLoginError,
    emailLoginOnly,
    showEmailLoginForm,
    clearAiLoginError,
    submitUniversalLogin,
    submitUniversalGoogleLogin,
    handleSchoolSelect,
    closeSchoolPicker,
  }
}
